<template>
  <div
    role="tablist"
    :class="
      cn(
        'tw:flex tw:items-center',
        variant === 'underline' ? 'tw:gap-3' : 'tw:gap-1',
        variant === 'underline' && rule && 'tw:border-b tw:border-solid tw:border-border-secondary',
        $attrs.class as any
      )
    "
    :style="$attrs.style as any">
    <button
      v-for="tab in tabs"
      :key="String(tab.value)"
      type="button"
      role="tab"
      :aria-selected="isActive(tab.value)"
      :data-testid="tab.testId ?? `tab-${tab.value}`"
      :class="
        cn(
          // shared (no bg here — bg is variant/state-driven below and setting
          // bg-transparent up top competes with bg-primary-lt on active boxed
          // tabs at equal specificity)
          'tw:inline-flex tw:cursor-pointer tw:items-center tw:gap-2 tw:border-0 tw:text-14 tw:font-semibold tw:no-underline! tw:transition-colors tw:duration-150',
          // underline: the 2px transparent border is always present so activating a tab does not shift the row
          variant === 'underline' && [
            'tw:bg-transparent tw:border-b-2 tw:border-solid tw:px-1 tw:pb-3 tw:pt-0',
            isActive(tab.value)
              ? 'tw:border-primary tw:text-primary'
              : 'tw:border-transparent tw:text-gray-500 tw:hover:text-gray-700',
          ],
          // boxed: primary-lt tab, no bottom border
          variant === 'boxed' && [
            'tw:rounded-md! tw:px-3 tw:py-2',
            isActive(tab.value)
              ? 'tw:bg-primary-lt tw:text-primary'
              : 'tw:bg-transparent tw:text-gray-500 tw:hover:bg-gray-50 tw:hover:text-gray-700',
          ]
        )
      "
      @click="select(tab.value)">
      <slot name="leading" :tab="tab" :active="isActive(tab.value)">
        <component v-if="tab.icon" :is="tab.icon" :size="16" :stroke-width="2" class="tw:shrink-0" />
      </slot>

      <slot name="label" :tab="tab" :active="isActive(tab.value)">
        <span>{{ tab.label }}</span>
      </slot>

      <slot name="trailing" :tab="tab" :active="isActive(tab.value)">
        <span
          v-if="tab.count != null"
          :class="[
            'tw:inline-flex tw:min-w-[18px] tw:items-center tw:justify-center tw:rounded-full tw:border tw:border-solid tw:px-[5px] tw:py-0 tw:text-12 tw:font-medium tw:leading-4',
            COUNT_TONE[tab.countTone ?? 'primary'],
          ]">
          {{ tab.count }}
        </span>
      </slot>
    </button>
  </div>
</template>

<script lang="ts">
import type { Component } from 'vue';

export type LeadTabsVariant = 'underline' | 'boxed';
export type LeadTabCountTone = 'primary' | 'neutral' | 'danger';

export interface LeadTabItem<V extends string | number = string | number> {
  value: V;
  label?: string;
  icon?: Component;
  /** Small count badge rendered after the label. */
  count?: string | number;
  /** Badge tone. Defaults to `primary`. Use `danger` only for genuine alarms. */
  countTone?: LeadTabCountTone;
  testId?: string;
}
</script>

<script setup lang="ts" generic="T extends string | number">
// LeadTabs — mirrors the LEAD Design System <TabsList> + <Tab> composition
// (see pjas-lead-designsystem/src/components/Tabs/Tabs.tsx).
//
// Two variants:
//   - `underline` (default): page-level sub-navigation. Every tab carries a
//     2px transparent bottom border so activating a tab does not shift the
//     row; the active tab swaps that border to `primary`.
//   - `boxed`: tabs inside a card. primary-lt fill on the active tab, no rule.
//
// API kept `tabs`-array driven for consumer compatibility. Three named slots
// let callers inject custom content while still leaning on the built-in
// defaults per tab: `#leading` (default: `tab.icon`), `#label` (default:
// `tab.label`), `#trailing` (default: `tab.count` count-badge).
import { cn } from '@/lib/cn';

defineOptions({ inheritAttrs: false });

withDefaults(
  defineProps<{
    tabs: LeadTabItem<T>[];
    variant?: LeadTabsVariant;
    /**
     * The `underline` bar's own bottom border. Set `false` when the container
     * already draws a rule under the bar (e.g. PageHeader is full-bleed) so
     * the two do not stack into a double line. Ignored by `boxed`.
     */
    rule?: boolean;
  }>(),
  {
    variant: 'underline',
    rule: true,
  }
);

const modelValue = defineModel<T>({ required: true });

// tone map for the built-in count badge. Same tinted-pill recipe as DS TabCount.
const COUNT_TONE: Record<LeadTabCountTone, string> = {
  primary: 'tw:bg-blue-50 tw:border-blue-200 tw:text-blue-500',
  neutral: 'tw:bg-white tw:border-pill-border tw:text-pill-fg',
  danger: 'tw:bg-red-50 tw:border-red-200 tw:text-red-600',
};

function isActive(value: T): boolean {
  return modelValue.value === value;
}

function select(value: T) {
  modelValue.value = value;
}
</script>
